import { useState } from 'react';
import {
  BookMarked,
  ChevronDown,
  ChevronRight,
  Database,
  ExternalLink,
  FileText,
  Globe,
  Wrench,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Citation } from '@/types/node-types';

const SOURCE_TYPE_META: Record<
  Citation['source_type'],
  { label: string; Icon: typeof Globe }
> = {
  web: { label: 'Web', Icon: Globe },
  deep_research: { label: 'Deep Research', Icon: BookMarked },
  collection: { label: 'Collection', Icon: Database },
  upload: { label: 'Upload', Icon: FileText },
  tool: { label: 'Tool', Icon: Wrench },
};

const CitationRow = ({ citation }: { citation: Citation }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = SOURCE_TYPE_META[citation.source_type] ?? SOURCE_TYPE_META.web;
  const hasExcerpts = (citation.excerpts?.length ?? 0) > 0;

  return (
    <li className='rounded-md border bg-muted/30 px-3 py-2 text-sm'>
      <div className='flex items-start gap-2'>
        <span className='shrink-0 font-mono text-xs text-muted-foreground mt-0.5'>
          {citation.marker ?? '•'}
        </span>
        <div className='min-w-0 flex-1'>
          <div className='flex flex-wrap items-center gap-2'>
            {citation.url ? (
              <a
                href={citation.url}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center gap-1 font-medium text-primary hover:underline break-all'
              >
                {citation.title}
                <ExternalLink className='size-3 shrink-0' />
              </a>
            ) : (
              <span className='font-medium'>{citation.title}</span>
            )}
            <Badge variant='outline' className='gap-1 text-[10px]'>
              <meta.Icon className='size-3' />
              {meta.label}
            </Badge>
            {citation.collection_name && (
              <span className='text-xs text-muted-foreground'>
                {citation.collection_name}
              </span>
            )}
            {typeof citation.retrieval_score === 'number' && (
              <span className='text-xs text-muted-foreground'>
                score {citation.retrieval_score.toFixed(2)}
              </span>
            )}
          </div>
          {hasExcerpts && (
            <button
              type='button'
              onClick={() => setExpanded((v) => !v)}
              className='mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground'
            >
              {expanded ? (
                <ChevronDown className='size-3' />
              ) : (
                <ChevronRight className='size-3' />
              )}
              {citation.excerpts?.length === 1
                ? 'Excerpt'
                : `${citation.excerpts?.length} excerpts`}
            </button>
          )}
          {expanded &&
            citation.excerpts?.map((excerpt, i) => (
              <blockquote
                key={i}
                className='mt-1 border-l-2 border-muted-foreground/30 pl-2 text-xs text-muted-foreground whitespace-pre-wrap'
              >
                {excerpt}
              </blockquote>
            ))}
        </div>
      </div>
    </li>
  );
};

interface CitationsListProps {
  citations?: Citation[] | null;
}

// Numbered to match the [n] markers in the generated content.
export const CitationsList = ({ citations }: CitationsListProps) => {
  const [open, setOpen] = useState(true);

  if (!citations || citations.length === 0) return null;

  return (
    <section className='mt-4' aria-label='Sources'>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        className='inline-flex items-center gap-1.5 text-sm font-medium text-foreground'
      >
        {open ? (
          <ChevronDown className='size-4' />
        ) : (
          <ChevronRight className='size-4' />
        )}
        Sources
        <Badge variant='secondary' className='text-[10px]'>
          {citations.length}
        </Badge>
      </button>
      {open && (
        <ul className='mt-2 space-y-2'>
          {citations.map((citation) => (
            <CitationRow
              key={citation.citation_id ?? citation.marker ?? citation.title}
              citation={citation}
            />
          ))}
        </ul>
      )}
    </section>
  );
};
